import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { auth } from "@/api/auth";
import { ApiError, TokenStore, request } from "@/api/http";
import { userMe, verification } from "@/api/passwords";
import { useAuth } from "@/auth/AuthContext";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormError,
  Input,
  Label,
} from "@/components/ui";
import { useToast } from "@/components/Toast";

export function ProfilePage() {
  const { user, refresh } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();

  // ----- profile fields -----
  const [fullName, setFullName] = useState("");
  const [locale, setLocale] = useState("");
  useEffect(() => {
    setFullName(user?.full_name ?? "");
    setLocale(user?.locale ?? "");
  }, [user]);

  const saveProfile = useMutation({
    mutationFn: () => userMe.patch({ full_name: fullName, locale }),
    onSuccess: async () => {
      toast.push("Profile updated", "success");
      await refresh();
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message : String(e), "error"),
  });

  // ----- password change -----
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwdError, setPwdError] = useState<string | null>(null);
  const changePwd = useMutation({
    mutationFn: () => userMe.patch({ password: newPassword }),
    onSuccess: () => {
      toast.push("Password changed", "success");
      setNewPassword("");
      setConfirmPassword("");
      setPwdError(null);
    },
    onError: (e) => setPwdError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  // ----- email verification -----
  const requestVerify = useMutation({
    mutationFn: () => verification.request(user?.email ?? ""),
    onSuccess: () => toast.push("Verification email sent", "success"),
    onError: (e) => toast.push(e instanceof ApiError ? e.message : String(e), "error"),
  });

  // ----- MFA -----
  const [enrollment, setEnrollment] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [mfaError, setMfaError] = useState<string | null>(null);

  const enroll = useMutation({
    mutationFn: () => auth.mfa.enroll(),
    onSuccess: (data) => {
      setEnrollment({ secret: data.secret, uri: data.provisioning_uri });
      setMfaError(null);
    },
    onError: (e) => setMfaError(e instanceof ApiError ? e.message : String(e)),
  });
  const verify = useMutation({
    mutationFn: (c: string) => auth.mfa.verify(c),
    onSuccess: async () => {
      setEnrollment(null);
      setCode("");
      toast.push("MFA enabled", "success");
      await refresh();
      qc.invalidateQueries();
    },
    onError: (e) => setMfaError(e instanceof ApiError ? e.message : String(e)),
  });
  const disable = useMutation({
    mutationFn: (c: string) => auth.mfa.disable(c),
    onSuccess: async () => {
      setCode("");
      toast.push("MFA disabled", "success");
      await refresh();
    },
    onError: (e) => setMfaError(e instanceof ApiError ? e.message : String(e)),
  });

  if (!user) return null;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">Profile</h2>
        </CardHeader>
        <CardBody className="space-y-3">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Field k="Email" v={user.email} />
            <Field k="Org" v={<code className="text-xs">{user.org_id}</code>} />
            <Field
              k="Status"
              v={user.is_active ? <Badge tone="good">active</Badge> : <Badge tone="bad">disabled</Badge>}
            />
            <Field
              k="Verified"
              v={
                user.is_verified ? (
                  <Badge tone="good">yes</Badge>
                ) : (
                  <span className="flex items-center gap-2">
                    <Badge tone="warn">no</Badge>
                    <button onClick={() => requestVerify.mutate()} className="text-xs text-brand">
                      resend
                    </button>
                  </span>
                )
              }
            />
            <Field k="Superuser" v={user.is_superuser ? <Badge tone="good">yes</Badge> : <Badge>no</Badge>} />
            <Field k="MFA" v={user.totp_enabled ? <Badge tone="good">enabled</Badge> : <Badge>disabled</Badge>} />
          </dl>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveProfile.mutate();
            }}
            className="space-y-3 border-t border-slate-200 dark:border-slate-800 pt-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Full name</Label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div>
                <Label>Locale</Label>
                <Input value={locale} onChange={(e) => setLocale(e.target.value)} placeholder="en-US" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" loading={saveProfile.isPending}>
                Save profile
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">Change password</h2>
        </CardHeader>
        <CardBody>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPwdError(null);
              if (newPassword !== confirmPassword) {
                setPwdError("Passwords don't match");
                return;
              }
              if (newPassword.length < 8) {
                setPwdError("Password must be at least 8 characters");
                return;
              }
              changePwd.mutate();
            }}
            className="space-y-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>New password</Label>
                <Input
                  type="password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div>
                <Label>Confirm</Label>
                <Input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>
            <FormError>{pwdError}</FormError>
            <div className="flex justify-end">
              <Button type="submit" loading={changePwd.isPending}>
                Update password
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">Two-factor authentication</h2>
        </CardHeader>
        <CardBody className="space-y-3">
          {!user.totp_enabled && !enrollment && (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Enable a TOTP authenticator for an extra layer of security on login.
              </p>
              <Button onClick={() => enroll.mutate()} loading={enroll.isPending}>
                Start MFA enrollment
              </Button>
            </>
          )}
          {enrollment && (
            <div className="space-y-3">
              <p className="text-sm">
                Add this secret to your authenticator app, then enter the current 6-digit code below
                to confirm.
              </p>
              <code className="block break-all rounded-md bg-slate-100 dark:bg-slate-800 p-2 text-xs">
                {enrollment.secret}
              </code>
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer">otpauth URI</summary>
                <code className="mt-1 block break-all">{enrollment.uri}</code>
              </details>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label htmlFor="verify-code">6-digit code</Label>
                  <Input
                    id="verify-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <Button
                  onClick={() => verify.mutate(code)}
                  loading={verify.isPending}
                  disabled={code.length !== 6}
                >
                  Verify
                </Button>
              </div>
            </div>
          )}
          {user.totp_enabled && !enrollment && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                MFA is enabled. To turn it off, enter a current TOTP code.
              </p>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label htmlFor="disable-code">Current code</Label>
                  <Input
                    id="disable-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <Button variant="danger" onClick={() => disable.mutate(code)} loading={disable.isPending}>
                  Disable MFA
                </Button>
              </div>
            </div>
          )}
          <FormError>{mfaError}</FormError>
        </CardBody>
      </Card>

      {/* Phase 10A.12 — GDPR self-serve. */}
      <GdprCard />
    </div>
  );
}

function GdprCard() {
  const { logout } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState<"export" | "delete" | null>(null);

  async function exportData() {
    setBusy("export");
    try {
      const token = TokenStore.get();
      const resp = await fetch("/api/v1/me/export", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        toast.push(`Export failed (${resp.status})`, "error");
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `piggy-data-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    } finally {
      setBusy(null);
    }
  }

  async function deleteAccount() {
    if (!confirm(
      "Delete your account? This is irreversible. Open positions must be closed first; trading-records (orders, fills, ledger) are retained per regulatory obligation.",
    )) return;
    setBusy("delete");
    try {
      const r = await request<{ status: string }>("/api/v1/me/delete", { method: "POST" });
      toast.push("Account deleted", "warn");
      if (r.status === "deleted") {
        logout();
      }
    } catch (e) {
      toast.push(
        e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e),
        "error",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold">Privacy &amp; data rights (GDPR)</h2>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-slate-500">
          You can export a machine-readable copy of all personal data we hold,
          or request deletion of your account. Trading records (orders, fills,
          ledger) are retained per regulatory obligation even after deletion —
          GDPR Article 17(3)(b) explicitly permits this.
        </p>
        <div className="flex gap-2">
          <Button onClick={exportData} loading={busy === "export"}>
            Download my data (ZIP)
          </Button>
          <Button variant="danger" onClick={deleteAccount} loading={busy === "delete"}>
            Delete my account
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <>
      <dt className="text-slate-500">{k}</dt>
      <dd>{v}</dd>
    </>
  );
}
